import { useSession } from "../../app/session";
import { valuesOf, type Counter } from "../counters/ops";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Table2, BarChart3 } from "lucide-react";
import { registerViewType, type ViewProps } from "../../registry/viewTypes";
import { useDoc, useDocSelector } from "../../core/useDocSelector";
import { listTasks } from "../../core/tasks";
import { snapshotOf } from "../../core/ops";
import { groupsOf } from "../../core/structure";
import { C, timelogDocId } from "../../core/schema";
import { entriesOf, yearMonth } from "../../tracking/timelog";
import { completions, counterSeries, timePerGroup, wantHeatmap, type Table } from "./metrics";
import { rotationAdherence } from "../../core/circle";

export interface ChartConfig {
  /** "timePerGroup" | "completions" | "wantHeatmap" | "counter:<id>" */
  metric?: string;
  /** days (or weeks for completions) */
  range?: number;
}

const SERIES_VARS = ["--chart-1", "--chart-2", "--chart-3", "--chart-4", "--chart-5", "--chart-6"];

function cssVar(name: string): string {
  return getComputedStyle(document.documentElement).getPropertyValue(name).trim();
}

/**
 * Charts (spec §9.7 `chart`, ECharts): one axis, thin marks, a legend for ≥ 2 series, a hover
 * tooltip, and always a table view (the light palette has slots under 3:1, so the table is the
 * accessible route). Colour follows the group (fixed slot order), never its rank on a given day.
 */
export function ChartView({ config, ctx }: ViewProps<ChartConfig>) {
  const { t, i18n } = useTranslation();
  const metric = config.metric ?? "timePerGroup";
  const range = config.range ?? (metric === "completions" ? 8 : 14);
  const [asTable, setAsTable] = useState(false);
  const el = useRef<HTMLDivElement>(null);
  const now = Math.floor(ctx.now / 60_000) * 60_000; // charts refresh once a minute
  const me = useSession((st) => st.session?.profile?.id) ?? null; // a per-person habit charts my own values

  const thisMonth = useDoc(timelogDocId(ctx.workspaceId, yearMonth(new Date(now))));
  const prevMonth = useDoc(timelogDocId(ctx.workspaceId, yearMonth(new Date(now - range * 86_400_000))));
  const e1 = useDocSelector(thisMonth, (doc) => entriesOf(doc));
  const e2 = useDocSelector(prevMonth, (doc) => entriesOf(doc));
  const core = useDocSelector(ctx.handle, (doc) => {
    const s = snapshotOf(doc);
    return {
      tasks: listTasks(doc),
      groupOf: Object.fromEntries(Object.keys(s.tasks).map((id) => [id, groupsOf(s, id)[0] ?? null])),
      groups: doc.getMap(C.groups).toJSON() as Record<string, { title: string }>,
      counters: doc.getMap(C.counters).toJSON() as Record<
        string,
        { title: string; unit?: string; entries: Record<string, number> }
      >,
      fairness: Boolean(
        (doc.getMap(C.settings).toJSON() as { circle?: { fairnessChart?: boolean } }).circle?.fairnessChart,
      ),
    };
  });

  const table: Table | null = useMemo(() => {
    if (!core) return null;
    if (metric === "rotationAdherence") {
      // fairness chart (spec §9.29): off unless enabled, never for kids; adherence, not totals per person
      if (!core.fairness || ctx.profileKind === "kid") return null;
      const [kept, total] = rotationAdherence(core.tasks);
      return {
        categories: [t("chart.turnsKept"), t("chart.turnsOther")],
        series: [{ id: "adherence", name: t("chart.metric.rotationAdherence"), data: [kept, total - kept] }],
        unit: "",
        kind: "bar" as const,
        plainCategories: true,
      };
    }
    if (metric === "completions") return completions(core.tasks, now, range, t("chart.completions"));
    if (metric === "wantHeatmap") return wantHeatmap(core.tasks, now, range);
    if (metric.startsWith("counter:")) {
      const c = core.counters[metric.slice(8)];
      return c ? counterSeries(valuesOf(c as Counter, me), c.title, c.unit ?? "", now, range) : null;
    }
    const entries = thisMonth?.docId === prevMonth?.docId ? (e1 ?? []) : [...(e1 ?? []), ...(e2 ?? [])];
    return timePerGroup(
      entries,
      (id) => core.groupOf[id] ?? null,
      (g) => core.groups[g]?.title ?? g,
      now,
      range,
      { none: t("chart.noGroup"), other: t("chart.other") },
    );
  }, [core, metric, range, now, e1, e2, thisMonth?.docId, prevMonth?.docId, t, ctx.profileKind, me]);

  const shortDay = (k: string) =>
    new Date(k + "T12:00:00").toLocaleDateString(i18n.language, { day: "numeric", month: "short" });
  const reduced =
    document.documentElement.dataset.reducedMotion === "true" ||
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  const themeKey = `${document.documentElement.dataset.theme ?? ""}|${document.documentElement.dataset.plainColors ?? ""}`;

  useEffect(() => {
    if (!el.current || !table || asTable) return;
    let disposed = false;
    let chart: { dispose(): void; resize(): void; setOption(o: unknown): void } | null = null;
    let ro: ResizeObserver | null = null;
    void import("echarts").then((echarts) => {
      if (disposed || !el.current) return;
      chart = echarts.init(el.current, undefined, { renderer: "svg" });
      const text = cssVar("--color-neutral-500");
      const line = cssVar("--line");
      const surface = cssVar("--surface");
      const colors = SERIES_VARS.map(cssVar);
      const base = {
        animation: !reduced,
        textStyle: { fontFamily: cssVar("--font-sans"), color: text },
        grid: {
          left: 36,
          right: 12,
          top: table.series.length > 1 && table.kind !== "heatmap" ? 28 : 12,
          bottom: 24,
          containLabel: false,
        },
        tooltip: {
          trigger: table.kind === "heatmap" ? "item" : "axis",
          axisPointer: { type: "shadow" },
          backgroundColor: surface,
          borderColor: line,
          textStyle: { color: cssVar("--color-neutral-900") },
        },
      };
      if (table.kind === "heatmap") {
        chart.setOption({
          ...base,
          grid: { left: 120, right: 12, top: 8, bottom: 40 },
          xAxis: {
            type: "category",
            data: table.categories.map(shortDay),
            axisLine: { lineStyle: { color: line } },
            axisLabel: { color: text },
            splitArea: { show: false },
          },
          yAxis: {
            type: "category",
            data: table.series.map((s) => s.name.slice(0, 18)),
            axisLine: { lineStyle: { color: line } },
            axisLabel: { color: text },
          },
          visualMap: {
            min: 0,
            max: 3,
            calculable: false,
            orient: "horizontal",
            left: "center",
            bottom: 0,
            itemHeight: 80,
            textStyle: { color: text },
            inRange: { color: [cssVar("--surface-2"), cssVar("--chart-seq")] },
          },
          series: [
            {
              type: "heatmap",
              data: table.series.flatMap((s, y) => s.data.map((v, x) => [x, y, v])),
              itemStyle: { borderColor: surface, borderWidth: 2 },
            },
          ],
        });
      } else {
        chart.setOption({
          ...base,
          color: colors,
          legend:
            table.series.length > 1
              ? {
                  top: 0,
                  left: 0,
                  icon: "roundRect",
                  itemWidth: 10,
                  itemHeight: 10,
                  textStyle: { color: text },
                }
              : undefined,
          xAxis: {
            type: "category",
            data: table.categories.map(shortDay),
            axisLine: { lineStyle: { color: line } },
            axisTick: { show: false },
            axisLabel: { color: text },
          },
          yAxis: {
            type: "value",
            name: table.unit,
            nameTextStyle: { color: text },
            splitLine: { lineStyle: { color: line } },
            axisLabel: { color: text },
          },
          series: table.series.map((s, i) => ({
            name: s.name,
            type: "bar",
            stack: table.kind === "stacked" ? "total" : undefined,
            data: s.data,
            barMaxWidth: 22,
            itemStyle: {
              color:
                s.id === "other" || s.id === "none"
                  ? cssVar("--chart-other")
                  : table.kind === "bar"
                    ? cssVar("--chart-seq")
                    : colors[i % colors.length],
              borderColor: surface,
              borderWidth: 1,
              borderRadius: table.kind === "bar" ? [4, 4, 0, 0] : 0,
            },
          })),
        });
      }
      ro = new ResizeObserver(() => chart?.resize());
      ro.observe(el.current);
    });
    return () => {
      disposed = true;
      ro?.disconnect();
      chart?.dispose();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [table, asTable, i18n.language, themeKey, reduced]);

  return (
    <div className="kss-surface flex h-full flex-col p-3" data-testid="chart-view" data-metric={metric}>
      <div className="mb-1 flex items-center justify-between">
        <h3 className="text-xs font-semibold uppercase tracking-[0.06em] text-neutral-500">
          {t(`chart.metric.${metric.split(":")[0]}`)}
        </h3>
        <button
          type="button"
          aria-pressed={asTable}
          aria-label={asTable ? t("chart.showChart") : t("chart.showTable")}
          title={asTable ? t("chart.showChart") : t("chart.showTable")}
          data-testid="chart-table-toggle"
          className="grid h-6 w-6 place-items-center rounded-sm text-neutral-500 hover:bg-neutral-100 dark:hover:bg-neutral-800"
          onClick={() => setAsTable(!asTable)}
        >
          {asTable ? <BarChart3 size={14} aria-hidden /> : <Table2 size={14} aria-hidden />}
        </button>
      </div>
      {!table || table.series.length === 0 ? (
        <p className="m-auto text-[12.5px] text-neutral-500">{t("chart.empty")}</p>
      ) : asTable ? (
        <div className="min-h-0 flex-1 overflow-auto">
          <table className="w-full text-[12px]" data-testid="chart-table">
            <thead>
              <tr>
                <th className="text-left font-medium text-neutral-500" />
                {table.series.map((s) => (
                  <th key={s.id} scope="col" className="px-2 text-right font-medium text-neutral-500">
                    {s.name}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {table.categories.map((c, i) => (
                <tr key={c} className="border-t border-neutral-200 dark:border-neutral-800">
                  <th scope="row" className="py-0.5 text-left font-normal">
                    {shortDay(c)}
                  </th>
                  {table.series.map((s) => (
                    <td key={s.id} className="px-2 text-right font-mono tabular-nums">
                      {s.data[i]}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div
          ref={el}
          className="min-h-0 flex-1"
          role="img"
          aria-label={t(`chart.metric.${metric.split(":")[0]}`)}
        />
      )}
    </div>
  );
}

registerViewType<ChartConfig>({
  id: "chart",
  label: "views.chart.label",
  icon: "bar-chart",
  level: "standard",
  defaultConfig: { metric: "timePerGroup", range: 14 },
  configSchema: {
    type: "object",
    properties: {
      metric: { type: "string", title: "config.metric", description: "config.metricHint" },
      range: { type: "number", title: "config.range", minimum: 1, maximum: 90 },
    },
  },
  Component: ChartView,
  minSize: { w: 4, h: 5 },
});
